import { Button, DatePicker, Dialog, FilterToolbar, notify, SearchInput, Select, StatusPill, TextField, Toggle, type FilterItem, type PillTone } from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { QuoteShare } from "./QuoteShare";

export type QuoteRow = {
  id: string;
  status: string;
  createdAt: string;
  expiresAt: string | null;
  originPostal: string;
  destinationPostal: string;
  pickupDate: string;
  orgId: string;
  orgName: string;
  prospect: boolean;
  createdBy: string | null;
  followUpOn: string | null;
  outcome: "open" | "won" | "lost";
  lostReason: string | null;
  lowest: { cents: number; currency: "USD" } | null;
  options: number;
};

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dateOnly = (iso: string) => day.format(new Date(`${iso}T12:00:00`));
const OUTCOME: Record<QuoteRow["outcome"], { label: string; tone: PillTone }> = {
  open: { label: "Open", tone: "info" },
  won: { label: "Won", tone: "success" },
  lost: { label: "Lost", tone: "neutral" },
};
const STATUS: Record<string, string> = { rated: "Rated", partial: "Partial", failed: "No rates", booked: "Booked", expired: "Expired", pending: "Pending" };
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Sales > Quotes: every quote across customers, with follow-up and re-quote. Filters live in the URL. */
export function QuotesList() {
  const me = useConsoleMe();
  const [params, setParams] = useSearchParams();
  const qs = {
    q: params.get("q") ?? "",
    status: params.get("status") ?? "any",
    outcome: params.get("outcome") ?? "any",
    mine: params.get("mine") === "1",
    due: params.get("due") === "1",
    page: Number(params.get("page") ?? "0") || 0,
  };
  const [search, setSearch] = useState(qs.q);
  const set = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || v === "any" || v === "0") next.delete(k);
          else next.set(k, v);
        }
        if (!("page" in patch)) next.delete("page");
        return next;
      },
      { replace: true },
    );
  useEffect(() => {
    const t = setTimeout(() => (search !== qs.q ? set({ q: search.trim() }) : undefined), 250);
    return () => clearTimeout(t);
  }, [search]); // only the typed text drives the debounce

  const query = new URLSearchParams({ q: qs.q, status: qs.status, outcome: qs.outcome, mine: qs.mine ? "1" : "0", due: qs.due ? "1" : "0", page: String(qs.page) });
  const list = useQuery({
    queryKey: ["console", "quotes", query.toString()],
    queryFn: () => api<{ quotes: QuoteRow[]; hasMore: boolean }>(`/v1/console/quotes?${query}`),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<QuoteRow | null>(null);
  const anyActive = qs.q !== "" || qs.status !== "any" || qs.outcome !== "any" || qs.mine || qs.due;

  const items: FilterItem[] = [
    {
      key: "outcome",
      priority: 1,
      active: qs.outcome !== "any",
      inline: <Select variant="toolbar" label="Outcome" value={qs.outcome} onChange={(v) => set({ outcome: v })} options={[{ value: "any", label: "Any" }, { value: "open", label: "Open" }, { value: "won", label: "Won" }, { value: "lost", label: "Lost" }]} />,
    },
    {
      key: "status",
      priority: 2,
      active: qs.status !== "any",
      inline: <Select variant="toolbar" label="Status" value={qs.status} onChange={(v) => set({ status: v })} options={[{ value: "any", label: "Any" }, { value: "rated", label: "Rated" }, { value: "partial", label: "Partial" }, { value: "failed", label: "No rates" }, { value: "booked", label: "Booked" }, { value: "expired", label: "Expired" }]} />,
    },
    { key: "mine", priority: 3, active: qs.mine, inline: <Toggle label="My quotes" checked={qs.mine} onChange={(on) => set({ mine: on ? "1" : null })} /> },
    { key: "due", priority: 4, active: qs.due, inline: <Toggle label="Follow-up due" checked={qs.due} onChange={(on) => set({ due: on ? "1" : null })} /> },
  ];

  const rows = list.data?.quotes ?? [];
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Quotes</h1>
          <p className="ffa-head__sub">Every quote across customers and prospects, with follow-ups and outcomes.</p>
        </div>
        {me.capabilities.includes("quotes:create") ? (
          <Link className="ffx-btn ffx-btn--primary" to="/quotes/new">
            New quote
          </Link>
        ) : null}
      </header>

      <FilterToolbar
        label="Quote filters"
        search={<SearchInput label="Search customer or ZIP" value={search} onChange={setSearch} />}
        items={items}
        anyActive={anyActive}
        onReset={() => {
          setSearch("");
          setParams(new URLSearchParams(), { replace: true });
        }}
      />

      {list.isError ? (
        <div className="ffa-notice" role="alert">
          <span>Couldn&apos;t load quotes. {message(list.error)}</span>
          <Button size="sm" onClick={() => void list.refetch()}>
            Try again
          </Button>
        </div>
      ) : null}

      <div className="ffx-table-wrap ffa-tablewrap" aria-busy={list.isFetching}>
        <table className="ffx-table ffa-table">
          <caption className="ffx-sr">Quotes</caption>
          <thead>
            <tr>
              <th scope="col">Customer</th>
              <th scope="col">Lane</th>
              <th scope="col">Pickup</th>
              <th scope="col" className="ffx-table__num">Lowest</th>
              <th scope="col" className="ffa-col-wide">Status</th>
              <th scope="col">Outcome</th>
              <th scope="col">Follow-up</th>
              <th scope="col" className="ffa-col-wide">Created by</th>
              <th scope="col">
                <span className="ffx-sr">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.isPending ? (
              <tr>
                <td colSpan={9} className="ffx-table__empty">
                  Loading quotes...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="ffx-table__empty">
                  {anyActive ? "No quotes match these filters." : "No quotes yet. Start one in the quoting center."}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td data-label="Customer">
                    <span className="ffa-cell-strong ffa-trunc" title={r.orgName}>
                      {r.orgName}
                    </span>
                    {r.prospect ? <StatusPill tone="warning">Prospect</StatusPill> : null}
                  </td>
                  <td data-label="Lane" className="ffx-num">
                    {r.originPostal} → {r.destinationPostal}
                  </td>
                  <td data-label="Pickup">
                    <time dateTime={r.pickupDate}>{dateOnly(r.pickupDate)}</time>
                  </td>
                  <td data-label="Lowest" className="ffx-table__num ffx-num">
                    {r.lowest ? usd.format(r.lowest.cents / 100) : "-"}
                  </td>
                  <td data-label="Status" className="ffa-col-wide">{STATUS[r.status] ?? r.status}</td>
                  <td data-label="Outcome">
                    <StatusPill tone={OUTCOME[r.outcome].tone}>{OUTCOME[r.outcome].label}</StatusPill>
                  </td>
                  <td data-label="Follow-up">{r.followUpOn ? <time dateTime={r.followUpOn}>{dateOnly(r.followUpOn)}</time> : <span className="ffa-muted">None</span>}</td>
                  <td data-label="Created by" className="ffa-col-wide">{r.createdBy ?? <span className="ffa-muted">API</span>}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setOpen(r)} aria-label={`Follow up on ${r.orgName} quote`}>
                      Follow up
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <nav className="ffa-pager" aria-label="Pages">
        <Button size="sm" variant="ghost" disabled={qs.page === 0} onClick={() => set({ page: String(qs.page - 1) })}>
          Previous
        </Button>
        <span className="ffa-muted">Page {qs.page + 1}</span>
        <Button size="sm" variant="ghost" disabled={!list.data?.hasMore} onClick={() => set({ page: String(qs.page + 1) })}>
          Next
        </Button>
      </nav>

      {open ? <FollowUpDialog row={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function FollowUpDialog({ row, onClose }: { row: QuoteRow; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [followUpOn, setFollowUpOn] = useState<string | null>(row.followUpOn);
  const [outcome, setOutcome] = useState<QuoteRow["outcome"]>(row.outcome);
  const [lostReason, setLostReason] = useState(row.lostReason ?? "");
  const save = useMutation({
    mutationFn: () =>
      api(`/v1/console/quotes/${row.id}/follow-up`, {
        method: "PATCH",
        body: { followUpOn, outcome, ...(outcome === "lost" ? { lostReason: lostReason.trim() } : {}) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "quotes"] });
      notify.success("Follow-up saved");
      onClose();
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const requote = useMutation({
    mutationFn: () => api<{ quoteId: string; options: number }>(`/v1/console/quotes/${row.id}/requote`, { method: "POST", body: {} }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "quotes"] });
      notify.success("Re-quoted", `${r.options} new rate${r.options === 1 ? "" : "s"}`);
      onClose();
      navigate(`/quotes?q=${encodeURIComponent(row.orgName)}`);
    },
    onError: (e) => notify.error("Couldn't re-quote", message(e)),
  });
  const valid = outcome !== "lost" || lostReason.trim().length >= 3;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${row.orgName}: ${row.originPostal} → ${row.destinationPostal}`}
      description={row.lowest ? `Lowest rate ${usd.format(row.lowest.cents / 100)} across ${row.options} option${row.options === 1 ? "" : "s"}.` : "No rates on this quote."}
      footer={
        <>
          <Button variant="ghost" loading={requote.isPending} onClick={() => requote.mutate()}>
            Re-quote
          </Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="ffa-form">
        <QuoteShare quoteId={row.id} />
        <DatePicker label="Follow up on" value={followUpOn} onChange={setFollowUpOn} variant="field" cutoffHour={null} />
        <Select label="Outcome" value={outcome} onChange={(v) => setOutcome(v)} options={[{ value: "open", label: "Open" }, { value: "won", label: "Won" }, { value: "lost", label: "Lost" }]} />
        {outcome === "lost" ? <TextField label="Why was it lost?" value={lostReason} onChange={(e) => setLostReason(e.target.value)} hint="For example: price, transit time, went with another broker" /> : null}
      </div>
    </Dialog>
  );
}
